import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Button, message, Table, Tabs } from 'antd';
import type { TableColumnsType } from 'antd';
import dayjs from 'dayjs';
import ScheduleFilter, { FilterOptions, DEFAULT_FILTER_OPTIONS } from './components/ScheduleFilter';
import CreateScheduleDrawer from './components/CreateScheduleDrawer';
import EditScheduleDrawer from './components/EditScheduleDrawer';
import RouteDetailDrawer from './components/RouteDetailDrawer';
import { useQuery, useLazyQuery, gql } from '@apollo/client';
import {
    GET_TMS_SCHEDULE_LISTING,
    GET_TMS_SCHEDULE_BY_ID,
    GetTmsScheduleByIdResponse,
    TmsScheduleFiltersInput,
    TmsScheduleListingResponse
} from './graphql/queries/getTmsSchedules';
import {
    GET_TMS_ROUTE_BY_ID,
    GetTmsRouteByIdResponse,
    GET_TMS_ROUTE_WITH_CARRIER_INFO_BY_ID,
    GetTmsRouteWithCarrierInfoByIdResponse,
    GET_TMS_ROUTE_WAYPOINTS_WITH_SORTING_CODE_BY_ROUTE_ID,
    GetTmsRouteWaypointsWithSortingCodeByRouteIdResponse,
} from '../route/graphql/queries/getTmsRoutes';
import { GET_TMS_CARRIERS_BY_WAREHOUSE_ID, GetTmsCarriersByWarehouseIdResponse } from './graphql/queries/getTmsCarriers';
import { GET_HANDOVER_CARRIERS_BY_WAREHOUSE_ID, GetHandoverCarriersByWarehouseIdResponse } from '../route/graphql/queries/getHandoverCarriers';
import { formatISODateTime } from "../../../utils/dateTimeUtils";
import {
    TmsCarrierSourceTypeEnum,
    TmsBusinessTypeEnum,
    TmsRouteTypeEnum,
    TmsStatusEnum,
    HandoverTypeEnum,
} from "../../../enums/TmsEnum";
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import i18n, { t } from '../../../i18n';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { useVehicleTypes } from '../../../hooks/useVehicleTypeOptions';

const GET_WAREHOUSES = gql`
    query GetWarehouses {
        warehouses: getWarehouseListForHeadquarters {
            id
            name
            sortingCode
            siteType
        }
    }
`;

const SchedulePage: React.FC = () => {
    // 从 WarehouseContext 获取当前选中的 Hub（TmsLayout 提供）
    const { currentWarehouse } = useWarehouseContext();
    const { vehicleTypes } = useVehicleTypes();
    
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    // 初始化时默认选中干线（routeType = 1）
    const [filters, setFilters] = useState<FilterOptions>({
        ...DEFAULT_FILTER_OPTIONS,
        routeType: String(TmsRouteTypeEnum.Linehaul)
    });
    const [tempFilters, setTempFilters] = useState<FilterOptions>({
        ...DEFAULT_FILTER_OPTIONS,
        routeType: String(TmsRouteTypeEnum.Linehaul)
    });
    const [createDrawerVisible, setCreateDrawerVisible] = useState(false);
    const [editDrawerVisible, setEditDrawerVisible] = useState(false);
    const [editRouteName, setEditRouteName] = useState<string>('');
    const [routeDetailDrawerVisible, setRouteDetailDrawerVisible] = useState(false);
    const [selectedRouteDetail, setSelectedRouteDetail] = useState<any>(null);

    const activeRouteTypeTab = filters.routeType ?? String(TmsRouteTypeEnum.Linehaul);
    const isHandoverTab = activeRouteTypeTab === String(TmsRouteTypeEnum.Handover);

    // Query warehouses to get sorting code mapping
    const { data: warehousesData, loading: warehousesLoading } = useQuery(GET_WAREHOUSES);

    // Query carriers for filter dropdown
    const { data: carriersData } = useQuery<GetTmsCarriersByWarehouseIdResponse>(GET_TMS_CARRIERS_BY_WAREHOUSE_ID, {
        variables: {
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : null
        },
        skip: !currentWarehouse?.id
    });

    // 查询交接方
    const { data: handoverCarriersData } = useQuery<GetHandoverCarriersByWarehouseIdResponse>(GET_HANDOVER_CARRIERS_BY_WAREHOUSE_ID, {
        variables: {
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : 0
        },
        skip: !isHandoverTab || !currentWarehouse?.id
    });

    const handoverCarrierOptions = useMemo(() => {
        const list = handoverCarriersData?.getHandoverCarriersByWarehouseId ?? [];
        return list.map(hc => ({ label: hc.carrierName, value: hc.id }));
    }, [handoverCarriersData]);

    // Create warehouseOptions array for form drawer
    const warehouseOptions = useMemo(() => {
        if (!warehousesData?.warehouses) return [];
        return warehousesData.warehouses.map((w: { id: string; sortingCode: string }) => ({ id: w.id, sortingCode: w.sortingCode }));
    }, [warehousesData]);

    // Create carrierOptions for filter dropdown
    useMemo(() => {
        if (!carriersData?.getTmsCarriersByWarehouseId) return [];
        return carriersData.getTmsCarriersByWarehouseId.map(carrier => ({
            label: carrier.carrierName,
            value: carrier.id
        }));
    }, [carriersData]);
// Create id -> sortingCode mapping
    const warehouseSortingCodeMap = useMemo(() => {
        const map = new Map<number, string>();
        if (warehousesData?.warehouses) {
            warehousesData.warehouses.forEach((warehouse: { id: string; sortingCode: string }) => {
                map.set(parseInt(warehouse.id), warehouse.sortingCode);
            });
        }
        return map;
    }, [warehousesData]);

    // 构建查询过滤条件
    const queryFilters: TmsScheduleFiltersInput = useMemo(() => {
        return {
            scheduleId: filters.scheduleId ? Number(filters.scheduleId): undefined,
            routeId: filters.routeId ? Number(filters.routeId): undefined,
            routeType: filters.routeType ? Number(filters.routeType): undefined,
            carrierId: filters.carrierId ? Number(filters.carrierId): undefined,
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : undefined,
            status: filters.status ? Number(filters.status): undefined
        };
    }, [filters, currentWarehouse?.id]);

    // Query for schedule listing
    const { data, loading, refetch } = useQuery<TmsScheduleListingResponse>(GET_TMS_SCHEDULE_LISTING, {
        variables: {
            filter: queryFilters,
            pageRequest: {
                page: currentPage,
                size: pageSize,
            }
        },
        fetchPolicy: 'no-cache',
        skip: !currentWarehouse?.id
    });

    const scheduleData = data?.getTmsScheduleListing?.content || [];
    const totalElements = data?.getTmsScheduleListing?.totalElements || 0;

    // 确保数据完整性：检查数据是否已完全加载
    const isDataReady = useMemo(() => {
        if (loading) return false;
        if (!data?.getTmsScheduleListing) return false;
        // 检查数据中是否所有记录都有必要的字段
        return scheduleData.every((record: any) => {
            // 基本字段必须存在
            return record?.id && record?.scheduleName && record?.routeId;
        });
    }, [loading, data, scheduleData]);

    // 计算第一列（路线列）的 rowSpan，用于合并相同值的单元格
    // 按路线ID和名称一起进行合并，名称和ID都相同才合并
    const routeRowSpanMap = useMemo(() => {
        const map = new Map<number, { rowSpan: number; isFirst: boolean }>();
        if (scheduleData.length === 0) return map;

        let currentRouteId: string | number | null = null;
        let currentRouteName: string | null = null;
        let startIndex = 0;
        let count = 0;

        scheduleData.forEach((record: any, index: number) => {
            const routeId = record.routeId;
            const routeName = record.routeName;
            // 只有当 routeId 和 routeName 都相同时才合并
            if (routeId === currentRouteId && routeName === currentRouteName) {
                count++;
            } else {
                // 处理上一个相同值的组
                if (currentRouteId !== null && currentRouteName !== null && count > 0) {
                    map.set(startIndex, { rowSpan: count, isFirst: true });
                    for (let i = startIndex + 1; i < startIndex + count; i++) {
                        map.set(i, { rowSpan: 0, isFirst: false });
                    }
                }
                // 开始新的组
                currentRouteId = routeId;
                currentRouteName = routeName;
                startIndex = index;
                count = 1;
            }
        });

        // 处理最后一组
        if (currentRouteId !== null && currentRouteName !== null && count > 0) {
            map.set(startIndex, { rowSpan: count, isFirst: true });
            for (let i = startIndex + 1; i < startIndex + count; i++) {
                map.set(i, { rowSpan: 0, isFirst: false });
            }
        }

        return map;
    }, [scheduleData]);

    const statusOptions = useMemo(() => [
        { label: t('tms.schedule.status.active'), value: TmsStatusEnum.Active },
        { label: t('tms.schedule.status.inactive'), value: TmsStatusEnum.InActive }
    ], [t]);

    // 当 Hub 改变时，重置分页到第一页
    useEffect(() => {
        if (currentWarehouse?.id) {
            setCurrentPage(1);
        }
    }, [currentWarehouse?.id]);

    const routeTypeOptions = useMemo(() => [
        { label: t('tms.route.routeTypes.linehaul'), value: TmsRouteTypeEnum.Linehaul },
        { label: t('tms.route.routeTypes.feeder'), value: TmsRouteTypeEnum.Feeder },
        { label: t('tms.route.routeTypes.handover'), value: TmsRouteTypeEnum.Handover },
    ], [t]);

    const businessTypeOptions = useMemo(() => [
        { label: t('tms.route.businessTypes.ownRoute'), value: TmsBusinessTypeEnum.OwnRoute },
        { label: t('tms.route.businessTypes.tiktok'), value: TmsBusinessTypeEnum.TikTok },
        { label: t('tms.route.businessTypes.temuGA'), value: TmsBusinessTypeEnum.TemuGA },
        { label: t('tms.route.businessTypes.temuSCF'), value: TmsBusinessTypeEnum.TemuSCF },
    ], [t]);

    const handleRouteTypeTabChange = (key: string) => {
        // 切换 tab 时清空路线选项、班次选项和承运商选项，然后根据条件进行查询
        const nextFilters: FilterOptions = {
            ...filters,
            routeType: key,
            scheduleId: '', // 清空班次选项
            routeId: '', // 清空路线选项
            carrierId: '' // 清空承运商选项
        };
        setTempFilters(nextFilters);
        setFilters(nextFilters);
        setCurrentPage(1);
    };

    const handleFilter = () => {
        setFilters({ ...tempFilters });
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        const defaultRouteType = filters.routeType || String(TmsRouteTypeEnum.Linehaul);
        const resetFiltersValue: FilterOptions = {
            ...DEFAULT_FILTER_OPTIONS,
            routeType: defaultRouteType
        };
        setTempFilters(resetFiltersValue);
        setFilters(resetFiltersValue);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        if (size && size !== pageSize) {
            setPageSize(size);
            setCurrentPage(1);
        }
    };

    const [getScheduleById, { data: scheduleDetailData }] = useLazyQuery<GetTmsScheduleByIdResponse>(
        GET_TMS_SCHEDULE_BY_ID,
        {
            fetchPolicy: 'no-cache',
        }
    );

    // 编辑抽屉用：取路线基本信息（不含 carrierName）
    const [getRouteById, { data: routeData }] = useLazyQuery<GetTmsRouteByIdResponse>(
        GET_TMS_ROUTE_BY_ID,
        {
            fetchPolicy: 'no-cache',
        }
    );

    // 详情抽屉用：后端联表直接返回 carrierName（不过滤 status，禁用交接方也能正常回显）
    const [getRouteWithCarrierInfoById] = useLazyQuery<GetTmsRouteWithCarrierInfoByIdResponse>(
        GET_TMS_ROUTE_WITH_CARRIER_INFO_BY_ID,
        {
            fetchPolicy: 'no-cache',
        }
    );

    // 统一的 route waypoints 查询（用于编辑抽屉和路由详情抽屉）
    const [getRouteWaypoints, { data: waypointsData }] = useLazyQuery<GetTmsRouteWaypointsWithSortingCodeByRouteIdResponse>(
        GET_TMS_ROUTE_WAYPOINTS_WITH_SORTING_CODE_BY_ROUTE_ID,
        {
            fetchPolicy: 'no-cache',
        }
    );


    // 当 schedule 查询完成后，根据 routeId 查询路线信息和途经点
    useEffect(() => {
        if (scheduleDetailData?.getTmsScheduleById?.routeId) {
            const routeId = scheduleDetailData.getTmsScheduleById.routeId;
            getRouteById({ variables: { id: routeId.toString() } });
            getRouteWaypoints({ variables: { routeId } });
        }
    }, [scheduleDetailData, getRouteById, getRouteWaypoints]);

    // 获取路线信息（用于显示总用时和总里程）
    const routeInfo = useMemo(() => {
        if (!routeData?.getTmsRouteById) return undefined;
        return {
            plannedTotalHours: routeData.getTmsRouteById.plannedTotalHours,
            plannedTotalMiles: routeData.getTmsRouteById.plannedTotalMiles,
            routeType: routeData.getTmsRouteById.routeType,
        };
    }, [routeData]);

    const showEditDrawer = (record: any) => {
        const scheduleId = typeof record.id === 'string' ? record.id : record.id.toString();
        setEditRouteName(record.routeName || '');
        setEditDrawerVisible(true);
        getScheduleById({ variables: { id: scheduleId } });
    };

    const showRouteDetailDrawer = useCallback(async (record: any) => {
        setSelectedRouteDetail(null);
        setRouteDetailDrawerVisible(true);

        try {
            const routeId = typeof record.routeId === 'string' ? parseInt(record.routeId) : record.routeId;
            const routeIdStr = typeof record.routeId === 'string' ? record.routeId : record.routeId.toString();
            
            const [routeResult, waypointsResult] = await Promise.all([
                getRouteWithCarrierInfoById({ variables: { id: routeIdStr } }),
                getRouteWaypoints({ variables: { routeId }, fetchPolicy: 'no-cache' })
            ]);

            const routeDetail = routeResult.data?.getTmsRouteWithCarrierInfoById;
            const waypoints =
                waypointsResult.data?.getTmsRouteWaypointsWithSortingCodeByRouteId?.waypoints || [];

            if (routeDetail) {
                setSelectedRouteDetail({
                    ...routeDetail,
                    waypoints: waypoints
                });
            } else {
                message.error(t('tms.schedule.messages.routeDetailNotFound', { id: routeIdStr }));
                setRouteDetailDrawerVisible(false);
            }
        } catch (error) {
            console.error('Failed to load route details:', error);
            message.error(t('tms.schedule.messages.failedToLoadRouteDetails'));
            setRouteDetailDrawerVisible(false);
        }
    }, [getRouteWithCarrierInfoById, getRouteWaypoints]);


    const columns: TableColumnsType<any> = useMemo(() => [
        {
            title: t('tms.schedule.columns.route'),
            dataIndex: 'routeName',
            key: 'routeName',
            align: 'left' as const,
            width: 150,
            ellipsis: false,
            render: (routeName: string | null | undefined, record: any) => {
                if (!routeName || !record.routeId) return <span style={{ whiteSpace: 'nowrap' }}>-</span>;
                return (
                    <Button
                        type="link"
                        onClick={() => showRouteDetailDrawer(record)}
                        style={{ paddingLeft: 0 }}
                    >
                        <span style={{ whiteSpace: 'nowrap' }}>{routeName}</span>
                    </Button>
                );
            },
            onCell: (_: any, index?: number) => {
                if (index === undefined) return {};
                const rowSpanInfo = routeRowSpanMap.get(index);
                if (rowSpanInfo) {
                    return {
                        rowSpan: rowSpanInfo.rowSpan,
                    };
                }
                return { rowSpan: 1 };
            },
        },
        {
            title: t('tms.schedule.columns.businessType'),
            dataIndex: 'businessType',
            key: 'businessType',
            align: 'left' as const,
            width: 120,
            ellipsis: false,
            render: (businessType: number | null | undefined) => {
                const opt = businessTypeOptions.find(option => option.value === businessType);
                const text = opt?.label ?? '-';
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            },
            onCell: (_: any, index?: number) => {
                if (index === undefined) return {};
                const rowSpanInfo = routeRowSpanMap.get(index);
                if (rowSpanInfo) {
                    return {
                        rowSpan: rowSpanInfo.rowSpan,
                    };
                }
                return { rowSpan: 1 };
            },
        },
        {
            title: t('tms.schedule.columns.schedule'),
            dataIndex: 'scheduleName',
            key: 'scheduleName',
            align: 'left' as const,
            width: 180,
            ellipsis: false,
            render: (scheduleName: string, record: any) => (
                <Button
                    type="link"
                    onClick={() => showEditDrawer(record)}
                    style={{ paddingLeft: 0 }}
                >
                    <span style={{ whiteSpace: 'nowrap' }}>{scheduleName}</span>
                </Button>
            ),
        },
        {
            title: t('tms.schedule.columns.plannedDeparture'),
            dataIndex: 'planDepartureTime',
            key: 'planDepartureTime',
            align: 'left',
            render: (time: string, record: any) => {
                if (!time) return '-';
                const timeObj = dayjs(time, ['HH:mm:ss', 'HH:mm']);
                const formattedTime = timeObj.isValid() ? timeObj.format('HH:mm') : time;
                const timezoneDisplay = record.planDepartureTimezone;
                return (
                    <div>
                        {formattedTime}
                        {timezoneDisplay && <div>({timezoneDisplay})</div>}
                    </div>
                );
            }
        },
        {
            title: t('tms.schedule.columns.plannedArrival'),
            dataIndex: 'planArrivalTime',
            key: 'planArrivalTime',
            align: 'left',
            render: (time: string, record: any) => {
                if (!time) return '-';
                const timeObj = dayjs(time, ['HH:mm:ss', 'HH:mm']);
                const formattedTime = timeObj.isValid() ? timeObj.format('HH:mm') : time;
                // 显示日期偏移量
                let dayOffsetDisplay = '';
                if (record.arrivalDayOffset !== null && record.arrivalDayOffset !== undefined) {
                    if (record.arrivalDayOffset === 0) {
                        // 0 不显示
                        dayOffsetDisplay = '';
                    } else if (record.arrivalDayOffset === 1) {
                        dayOffsetDisplay = ' ' + t('tms.schedule.dayOffset.plusOne');
                    } else if (record.arrivalDayOffset > 1) {
                        dayOffsetDisplay = ' ' + t('tms.schedule.dayOffset.plusDays', { count: record.arrivalDayOffset });
                    } else if (record.arrivalDayOffset === -1) {
                        dayOffsetDisplay = ' ' + t('tms.schedule.dayOffset.minusOne');
                    } else if (record.arrivalDayOffset < -1) {
                        dayOffsetDisplay = ' ' + t('tms.schedule.dayOffset.minusDays', { count: Math.abs(record.arrivalDayOffset) });
                    }
                }
                const timezoneDisplay = record.planArrivalTimezone;
                return (
                    <div>
                        {`${formattedTime}${dayOffsetDisplay}`}
                        {timezoneDisplay && <div>({timezoneDisplay})</div>}
                    </div>
                );
            },
        },
        {
            title: t('tms.schedule.columns.departureFrequency'),
            dataIndex: 'departureFrequency',
            key: 'departureFrequency',
            align: 'left',
            width: 230,
            render: (frequencyJson: string) => {
                if (!frequencyJson) return '-';
                try {
                    const days = JSON.parse(frequencyJson) as number[];
                    // 1=Monday, 2=Tuesday, 3=Wednesday, 4=Thursday, 5=Friday, 6=Saturday, 7=Sunday
                    if (days.length === 7 && days.every((d, i) => d === i + 1)) {
                        return t('tms.schedule.frequency.daily');
                    }
                    const dayNames = [
                        t('tms.schedule.frequency.monday'),
                        t('tms.schedule.frequency.tuesday'),
                        t('tms.schedule.frequency.wednesday'),
                        t('tms.schedule.frequency.thursday'),
                        t('tms.schedule.frequency.friday'),
                        t('tms.schedule.frequency.saturday'),
                        t('tms.schedule.frequency.sunday')
                    ];
                    return days.map(day => dayNames[day - 1]).join(', ');
                } catch {
                    return frequencyJson;
                }
            },
        },
        // carrierSourceType 列已在交接方需求中废弃，非交接Tab不再展示
        ...(isHandoverTab ? [{
            title: t('tms.schedule.drawer.handoverCarrierType'),
            dataIndex: 'carrierSourceType',
            key: 'carrierSourceType',
            align: 'left' as const,
            ellipsis: false,
            render: (carrierSourceType?: number | null) => {
                let text = '-';
                if (carrierSourceType === HandoverTypeEnum.LocalLastMileCarrier) {
                    text = t('tms.route.handoverTypes.localLastMileCarrier');
                } else if (carrierSourceType === HandoverTypeEnum.LinehaulCarrier) {
                    text = t('tms.route.handoverTypes.linehaulCarrier');
                }
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        }] : []),
        {
            title: isHandoverTab ? t('tms.schedule.drawer.handoverCarrier') : t('tms.schedule.columns.carrier'),
            dataIndex: 'carrierName',
            key: 'carrierName',
            align: 'left' as const,
            ellipsis: false,
            render: (carrierName: string) => <span style={{ whiteSpace: 'nowrap' }}>{carrierName || '-'}</span>
        },
        {
            title: t('tms.schedule.columns.driver'),
            dataIndex: 'driverName',
            key: 'driverName',
            align: 'left' as const,
            ellipsis: false,
            render: (driverName: string | null | undefined, record: any) => {
                const text = driverName || record.driverId || '-';
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            },
        },
        {
            title: t('tms.schedule.columns.vehicle'),
            dataIndex: 'vehicleNumber',
            key: 'vehicleNumber',
            align: 'left' as const,
            ellipsis: false,
            render: (vehicleNumber: string) => <span style={{ whiteSpace: 'nowrap' }}>{vehicleNumber || '-'}</span>
        },
        {
            title: t('tms.schedule.columns.vehicleModel'),
            dataIndex: 'vehicleTypeId',
            key: 'vehicleTypeId',
            align: 'left' as const,
            ellipsis: false,
            render: (vehicleTypeId: number) => {
                const vt = vehicleTypes?.find(v => String(v.id) === String(vehicleTypeId));
                const name = vt ? vt.nameEn : undefined;
                return <span style={{ whiteSpace: 'nowrap' }}>{name || '-'}</span>;
            }
        },
        {
            title: t('tms.schedule.columns.status'),
            dataIndex: 'status',
            key: 'status',
            align: 'left' as const,
            ellipsis: false,
            render: (status: number) => {
                const statusOption = statusOptions.find(option => option.value === status);
                const text = statusOption?.label || t('tms.schedule.status.unknown');

                let color = '';
                switch (status) {
                    case 1:
                        color = 'text-green-600';
                        break;
                    case 0:
                        color = 'text-red-500';
                        break;
                    default:
                        color = 'text-gray-400';
                }

                return <span className={color} style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('tms.schedule.columns.updatedTime'),
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            align: 'left' as const,
            ellipsis: false,
            render: (value) => {
                const formatted = formatISODateTime(value);
                return <span style={{ whiteSpace: 'nowrap' }}>{formatted}</span>;
            }
        },
        {
            title: t('tms.schedule.columns.action'),
            key: 'action',
            width: 120,
            align: 'left' as const,
            fixed: 'right',
            ellipsis: false,
            render: (_, record: any) => {
                return (
                    <div className="flex gap-2">
                        <Button
                            type="link"
                            onClick={() => showEditDrawer(record)}
                            style={{ paddingLeft: 0 }}
                        >
                            <span style={{ whiteSpace: 'nowrap' }}>{t('tms.schedule.buttons.edit')}</span>
                        </Button>
                    </div>
                );
            }
        }
    ], [routeTypeOptions, businessTypeOptions, statusOptions, warehouseSortingCodeMap, showEditDrawer, showRouteDetailDrawer, routeRowSpanMap, t, vehicleTypes]);

    const showNewDrawer = () => {
        setCreateDrawerVisible(true);
    };

    const handleDrawerSubmit = async () => {
        // 关闭所有抽屉
        setCreateDrawerVisible(false);
        setEditDrawerVisible(false);
        
        // Trigger refetch to update the list while preserving filter state
        // Use network-only to ensure fresh data from server
        // 等待所有 refetch 完成，确保数据完全同步
        try {
            await refetch({
                filter: queryFilters,
                pageRequest: {
                    page: currentPage,
                    size: pageSize,
                }
            });
            // 添加短暂延迟，确保后端数据完全同步（特别是关联字段如 routeName）
            await new Promise(resolve => setTimeout(resolve, 100));
            // 再次 refetch 主列表，确保获取到最新的关联数据
            await refetch({
                filter: queryFilters,
                pageRequest: {
                    page: currentPage,
                    size: pageSize,
                }
            });
        } catch (error) {
            console.error('Failed to refetch data after submit:', error);
            // 即使 refetch 失败，也继续执行，避免用户界面卡住
        }
    };

    return (
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                {/* Filter Area */}
                <ScheduleFilter
                    statusOptions={statusOptions}
                    routeTypeOptions={routeTypeOptions}
                    carriersData={carriersData?.getTmsCarriersByWarehouseId}
                    handoverCarrierOptions={handoverCarrierOptions}
                    isHandoverTab={isHandoverTab}
                    currentRouteType={filters.routeType}
                    filters={tempFilters}
                    setFilters={setTempFilters}
                />

                {/* Action Buttons + Tabs */}
                <div className="flex justify-between items-center mb-3 pl-0">
                    {/* Route Type Tabs: 干线 / 支线 */}
                    <div>
                        <Tabs
                            activeKey={activeRouteTypeTab}
                            onChange={handleRouteTypeTabChange}
                            items={[
                                {
                                    key: String(TmsRouteTypeEnum.Linehaul),
                                    label: t('tms.route.routeTypes.linehaul'),
                                },
                                {
                                    key: String(TmsRouteTypeEnum.Feeder),
                                    label: t('tms.route.routeTypes.feeder'),
                                },
                                {
                                    key: String(TmsRouteTypeEnum.Handover),
                                    label: t('tms.route.routeTypes.handover'),
                                },
                            ]}
                            className="[&_.ant-tabs-tab]:font-bold"
                        />
                    </div>

                    {/* 批量操作区域 */}
                    <div className="flex gap-2">
                        <Button
                            type="primary"
                            onClick={showNewDrawer}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('tms.schedule.buttons.new')}</span>
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleFilter}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('tms.schedule.filters.filter')}</span>
                        </Button>
                        <Button
                            onClick={handleResetFilters}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('tms.schedule.filters.reset')}</span>
                        </Button>
                    </div>
                </div>

                {/* Table */}
                <Table
                    rowKey="id"
                    loading={loading || !isDataReady}
                    columns={columns}
                    dataSource={scheduleData}
                    scroll={{ x: 'max-content' }}
                    pagination={false}
                    size="middle"
                    tableLayout="auto"
                    className="mb-4"
                />

                {/* Pagination */}
                <TablePaginationBar
                    total={totalElements}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    showQuickJumper={false}
                />

                {/* Create Drawer */}
                <CreateScheduleDrawer
                    visible={createDrawerVisible}
                    onClose={() => setCreateDrawerVisible(false)}
                    onSubmit={handleDrawerSubmit}
                    statusOptions={statusOptions}
                    routeTypeOptions={routeTypeOptions}
                />

                {/* Edit Drawer */}
                <EditScheduleDrawer
                    visible={editDrawerVisible}
                    onClose={() => {
                        setEditDrawerVisible(false);
                    }}
                    onSubmit={handleDrawerSubmit}
                    initialValues={scheduleDetailData?.getTmsScheduleById || undefined}
                    waypoints={
                        waypointsData?.getTmsRouteWaypointsWithSortingCodeByRouteId?.waypoints || undefined
                    }
                    routeBusinessType={
                        waypointsData?.getTmsRouteWaypointsWithSortingCodeByRouteId?.routeBusinessType ?? null
                    }
                    routeInfo={routeInfo}
                    statusOptions={statusOptions}
                    routeName={editRouteName}
                    isHandoverTab={isHandoverTab}
                />

                {/* Route Detail Drawer */}
                <RouteDetailDrawer
                    visible={routeDetailDrawerVisible}
                    onClose={() => setRouteDetailDrawerVisible(false)}
                    initialValues={selectedRouteDetail || undefined}
                    statusOptions={statusOptions}
                    routeTypeOptions={routeTypeOptions}
                    warehouseOptions={warehouseOptions}
                    loading={warehousesLoading}
                    isHandoverTab={isHandoverTab}
                />
            </div>
    );
};

export default SchedulePage;

